import * as i0 from '@angular/core';
import { Directive, Input, InjectionToken, PLATFORM_ID, Injectable, Inject, Optional, Component, ChangeDetectionStrategy, ViewEncapsulation, ViewChild, ContentChild, Output, NgModule } from '@angular/core';
import { map, distinctUntilChanged, takeUntil, switchMap, debounce, distinctUntilKeyChanged } from 'rxjs/operators';
import * as i3 from '@angular/common';
import { isPlatformBrowser, DOCUMENT, CommonModule, NgForOf, AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { interval, BehaviorSubject, Observable, asyncScheduler, Subject, NEVER, combineLatest, of, Subscription } from 'rxjs';
import bezier from 'bezier-easing';

class CarouselSlideDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
        this.ngCarouselSlide = null;
    }
    static ngTemplateContextGuard(dir, ctx) {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselSlideDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.2", type: CarouselSlideDirective, selector: "[ngCarouselSlide]", inputs: { ngCarouselSlide: "ngCarouselSlide" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselSlideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngCarouselSlide]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; }, propDecorators: { ngCarouselSlide: [{
                type: Input
            }] } });

var CarouselAlignMode;
(function (CarouselAlignMode) {
    /**
     * Active slide is always centered in carousel viewport
     */
    CarouselAlignMode["CENTER"] = "center";
    /**
     * Active slide left border sticks to left border of
     * carousel
     */
    CarouselAlignMode["LEFT"] = "left";
})(CarouselAlignMode || (CarouselAlignMode = {}));

/**
 * Represents how carousel calculates slide width. Be advised
 * that actual enum value is used in code, so change with caution.
 */
var CarouselWidthMode;
(function (CarouselWidthMode) {
    /**
     * When pixel management is provided, carousel calculates
     * slide width with pixels. Preferred to use in limited space
     * where carousel width is more or less is predefined.
     */
    CarouselWidthMode["PX"] = "px";
    /**
     * When percent management is provided, carousel calculates
     * slide width with relative instrument (via percents). Preferred
     * to use in full-width carousel, where carousel width is unknown.
     */
    CarouselWidthMode["PERCENT"] = "%";
})(CarouselWidthMode || (CarouselWidthMode = {}));

class CompleteCarouselConfig {
    constructor(config) {
        /**
         * Array of data to display
         *
         * Default: []
         */
        this.items = [];
        /**
         * Describes how carousel calculates its content width.
         * Consult with corresponding enum to see what options
         * are available.
         *
         * Default: CarouselWidthMode.PERCENT
         */
        this.widthMode = CarouselWidthMode.PERCENT;
        /**
         * Slide width. It could be pixels or percents, based on mode
         * configuration.
         *
         * Default: 100
         */
        this.slideWidth = 100;
        /**
         * Describes how slides should be positioned relative to
         * carousel viewport
         *
         * Default: CarouselAlignMode.CENTER
         */
        this.alignMode = CarouselAlignMode.CENTER;
        /**
         * Whether autoplay is enabled
         *
         * Default: true
         */
        this.autoplayEnabled = true;
        /**
         * Time in ms of how long carousel would wait until automatic
         * slide increment. Respects `autoplayEnabled` value.
         *
         * Default: 6000
         */
        this.autoplayDelay = 6000;
        /**
         * Whether mouse drag or gesture panning enabled
         *
         * Default: true
         */
        this.dragEnabled = true;
        /**
         * Whether carousel should start from beginning after last
         * slide
         *
         * Default: true
         */
        this.shouldLoop = true;
        /**
         * Time in ms of how long transition between slides would last
         *
         * Default: 280
         */
        this.transitionDuration = 280;
        /**
         * Whether carousel should automatically recalculate on window resize.
         * This option is recommended when using pixel width mode or left
         * alignment.
         *
         * Default: true
         */
        this.shouldRecalculateOnResize = true;
        /**
         * Specifies time for which carousel would wait after resize event
         * to recalculate its positions. 0 means no debounce is applied.
         *
         * Default: 300
         */
        this.recalculateDebounce = 300;
        /**
         * Value in current width mode units which are virtally added to both sides
         * of viewport. Slides within this virtual zone should always be presented
         * whether loop mode is on.
         *
         * Default: 5
         */
        this.threshold = 5;
        /**
         * Whether carousel is listening to arrow keypresses and navigates to prev and
         * next slide accordingly after left or right arrow key is pressed
         */
        this.allowKeyboardNavigation = true;
        this.items = [...(config?.items ?? [])];
        this.widthMode = config?.widthMode ?? CarouselWidthMode.PERCENT;
        this.alignMode = config?.alignMode ?? CarouselAlignMode.CENTER;
        this.slideWidth = extractCoerced(config?.slideWidth, 100);
        this.autoplayEnabled = extractCoerced(config?.autoplayEnabled, true);
        this.autoplayDelay = extractCoerced(config?.autoplayDelay, 6000);
        this.dragEnabled = extractCoerced(config?.dragEnabled, true);
        this.shouldLoop = extractCoerced(config?.shouldLoop, true);
        this.transitionDuration = extractCoerced(config?.transitionDuration, 600);
        this.shouldRecalculateOnResize = extractCoerced(config?.shouldRecalculateOnResize, true);
        this.recalculateDebounce = extractCoerced(config?.recalculateDebounce, 300);
        this.allowKeyboardNavigation = extractCoerced(config?.allowKeyboardNavigation, true);
    }
}
function extractCoerced(value, defaultValue) {
    if (isBoolean(defaultValue)) {
        return isBoolean(value) ? value : defaultValue;
    }
    else if (isNumber(defaultValue)) {
        return isNumber(value) ? value : defaultValue;
    }
    return value ?? defaultValue;
}
function isBoolean(prop) {
    return typeof prop === 'boolean';
}
function isNumber(prop) {
    return isFinite(prop) && !isNaN(prop);
}

/**
 * Generator for `CarouselSlide` id, should be
 * singleton throughout single carousel.
 */
class IdGenerator {
    constructor() {
        this.index = 0;
    }
    next() {
        return this.index++;
    }
}

/**
 * Autoplay state of current autoplay timer
 */
class CarouselAutoplay {
    constructor() {
        this.autoplaySuspenders = new Set();
        this.autoplaySubscription = null;
        this.interval = null;
    }
}

/**
 * Since we don't want excessive slide recalculation
 * while main inputs are still on their way, we should
 * collect them and then initialize carousel when
 * everything is ready.
 */
class InitializationState {
    constructor() {
        this.configInitialized = false;
        this.viewportWidthInitialized = false;
        /** Whether slides were created for the first time */
        this.firstInitalization = false;
    }
}

/**
 * Carousel state snapshot. Config is stored here
 * as well as other vital values like offset or
 * active slide index.
 */
class CarouselState {
    constructor(state) {
        /**
         * Container to measure gallery width. Type is HTMLElement but
         * reduced for the ease of testing.
         */
        this.widthContainer = null;
        /** Container that should be animated during index change */
        this.animatableContainer = null;
        /** Client-side config which regulates carousel behavior */
        this.config = new CompleteCarouselConfig();
        this.activeSlideIndex = 0;
        /** Item index of config's items array */
        this.activeItemIndex = 0;
        this.template = null;
        /** X position of leftmost carousel slide */
        this.offset = 0;
        this.slides = [];
        /** State of multiphase initialization */
        this.initializationState = new InitializationState();
        /** Currently played animation */
        this.animation = null;
        this.autoplay = new CarouselAutoplay();
        this.dragBezierFn = null;
        /** Used to extract values to applied beziers */
        this.invertedDragBezierFn = null;
        this.animationBezierFn = null;
        /**
         * When no slides available and user intents to set index,
         * this field would be initialized with desired index, which
         * should be to activeSlideIndex when slides become available
         */
        this.postponedItemIndex = null;
        /** Whether drag is in process right now */
        this.isDragged = false;
        this.slideIndex = 0;
        if (state) {
            Object.assign(this, state);
        }
    }
}

class ProcedureError extends Error {
    constructor(error) {
        super();
        this.message = error.message;
        this.stack = error.stack;
    }
}

/** Organizes array of procedures into single procedure, flattens inner procedures */
function procedurePipe(procedureName, ...args) {
    return (parentState, parentProcedureName) => {
        let state = parentState.state;
        let procedureState = parentState.procedureState;
        let procedureIndex = 0;
        let shouldBreakProcedure = false;
        for (const procedure of args) {
            if (typeof procedure !== 'function') {
                continue;
            }
            const nextState = {
                state,
                procedureState,
                environment: parentState.environment,
            };
            const procedureSlot = `${procedureName}[${procedureIndex}]`;
            const procedureChainString = parentProcedureName
                ? `${parentProcedureName}->${procedureSlot}`
                : procedureSlot;
            let localHandler = null;
            while (typeof localHandler === 'function' || !localHandler) {
                // Recursively call procedure until all the inner functions
                // are unwrapped into object value
                try {
                    localHandler = !localHandler
                        ? procedure(nextState, procedureChainString)
                        : localHandler(nextState, procedureChainString);
                }
                catch (e) {
                    if (!(e instanceof ProcedureError)) {
                        console.error(`Procedure interrupted at ${procedureChainString}`);
                    }
                    throw new ProcedureError(e);
                }
            }
            state = localHandler.state;
            procedureState = {
                ...procedureState,
                ...(localHandler.procedureState || {}),
            };
            shouldBreakProcedure = localHandler.shouldBreakProcedure;
            procedureIndex++;
            if (shouldBreakProcedure) {
                break;
            }
        }
        return {
            state,
            procedureState,
            shouldBreakProcedure,
        };
    };
}

const SLIDE_ID_GENERATOR = new InjectionToken('slideIdGenerator');
const ANIMATION_ID_GENERATOR = new InjectionToken('slideIdGenerator');

class ContinueWith {
    constructor(state, procedureState = null) {
        this.state = state;
        this.procedureState = procedureState;
        this.shouldBreakProcedure = false;
    }
}

/** Stops autoplay timer, provides side effect for provided autoplay */
function disableAutoplay(suspender, autoplay = new CarouselAutoplay()) {
    autoplay?.autoplaySubscription?.unsubscribe();
    if (!autoplay.autoplaySuspenders) {
        autoplay.autoplaySuspenders = new Set();
    }
    autoplay.autoplaySuspenders.add(suspender);
    return autoplay;
}

/**
 * Turns current autoplay timer off with a specified reason
 */
function disableAutoplayProcedure(suspender) {
    return ({ state }) => {
        const autoplay = disableAutoplay(suspender, state.autoplay);
        const modifiedState = {
            ...state,
            autoplay,
        };
        return new ContinueWith(modifiedState);
    };
}

/** Starts carousel autoplay whether one is enabled */
function enableAutoplay(autoplayEnabled, transitionDuration, autoplayDelay, isBrowser, autoplayAction, suspender, autoplay, zone) {
    if (!autoplay) {
        autoplay = new CarouselAutoplay();
    }
    autoplay.autoplaySubscription?.unsubscribe();
    if (!autoplay.autoplaySuspenders) {
        autoplay.autoplaySuspenders = new Set();
    }
    if (suspender) {
        autoplay.autoplaySuspenders.delete(suspender);
    }
    if (autoplayEnabled && !autoplay.autoplaySuspenders.size && isBrowser) {
        // Delay can't be smaller than transition itself in order to avoid endless animation
        const autoplayInterval = Math.max(transitionDuration, autoplayDelay);
        autoplay.interval = autoplayInterval;
        if (zone) {
            zone.runOutsideAngular(() => {
                if (autoplay) {
                    autoplay.autoplaySubscription = interval(autoplayInterval).subscribe(autoplayAction);
                }
            });
        }
        else {
            autoplay.autoplaySubscription = interval(autoplay.interval).subscribe(autoplayAction);
        }
    }
    return autoplay;
}

/**
 * Enables autoplay when such option is available in config
 */
function enableAutoplayProcedure(suspender = null) {
    return ({ state, environment }) => {
        const autoplay = enableAutoplay(state.config.autoplayEnabled, state.config.transitionDuration, state.config.autoplayDelay, environment?.isBrowser ?? false, environment?.autoplayAction ?? (() => { }), suspender, state.autoplay, environment.zone);
        const modifiedState = {
            ...state,
            autoplay,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Width of carousel element in pixels. Try to call this as little as
 * possible, since getting this value triggers layout thrashing.
 */
function getViewportWidthInPx(state) {
    return state?.widthContainer?.offsetWidth ?? 100;
}

/**
 * Width of carousel element in current width mode.
 * Try to call this as little as possible, since getting this value
 * triggers layout thrashing.
 */
function getViewportWidth(state) {
    return state?.config?.widthMode === CarouselWidthMode.PX
        ? getViewportWidthInPx(state)
        : 100;
}

class CalculateOffsetResult {
    constructor(offset) {
        this.offset = offset;
    }
}

/**
 * Calculates offset by specified arguments as if current slide was centered
 */
function calculateOffset(currentSlideIndex, alignMode, slideWidth, viewportWidth, slideQuantity, shouldLoop) {
    const activeSlideLeftPosition = alignMode === CarouselAlignMode.CENTER
        ? (viewportWidth - slideWidth) / 2
        : 0;
    const slidesSumWidth = slideWidth * slideQuantity;
    let newOffset = activeSlideLeftPosition - (slideWidth * currentSlideIndex);
    // Edge case amendment for left-aligned non-looped slides:
    // non-looped slides might have their right or left edge visible
    // so we might amend offset for some situations
    if (!shouldLoop && alignMode === CarouselAlignMode.LEFT) {
        // Calculate prequesites
        const rightmostPoint = newOffset + slidesSumWidth;
        const allSlidesVisible = slidesSumWidth <= viewportWidth;
        // Apply offset amendment
        if (!allSlidesVisible && rightmostPoint <= viewportWidth) {
            // Left aligned slides with their right edge visible:
            // righmost slide edge should stick to the right viewport edge
            newOffset = viewportWidth - slidesSumWidth;
        }
        else if (allSlidesVisible) {
            // Left aligned slides which are all visible:
            // just stick them to the left viewport edge
            newOffset = 0;
        }
    }
    const result = new CalculateOffsetResult(Math.round(newOffset));
    return result;
}

/**
 * Given current slide state, assigns carousel offset
 */
function calculateOffsetProcedure() {
    return ({ state, procedureState }) => {
        const result = calculateOffset(state.activeSlideIndex, state.config.alignMode, state.config.slideWidth, getViewportWidth(state), state.slides.length, state.config.shouldLoop);
        const modifiedProcedureState = {
            ...procedureState,
            offsetSnapshot: state.offset,
        };
        const modifiedState = {
            ...state,
            offset: result.offset,
        };
        return new ContinueWith(modifiedState, modifiedProcedureState);
    };
}

/**
 * Destroys animation player without callback
 */
function destroyAnimation(animation) {
    animation?.player?.cancel();
}

/**
 * Destroys and removes any ongoing animation state
 */
function destroyAnimationProcedure() {
    return ({ state }) => {
        destroyAnimation(state.animation);
        const modifiedState = {
            ...state,
            animation: null,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Slide model representation
 */
class CarouselSlide {
    constructor(
    /**
     * Id is used in trackBy and should be generated
     * with `IdGenerator`
     */
    id, 
    /**
     * Represents index from `items` array of carousel
     * config
     */
    itemIndex, 
    /**
     * Parameters that might be changed over time
     */
    options) {
        this.id = id;
        this.itemIndex = itemIndex;
        this.options = options;
        this.context = {
            $implicit: this.options.item,
            itemIndex: this.itemIndex,
            isActive: this.options.isActive,
            inViewport: this.options.inViewport,
            activeOnTheLeft: this.options.activeOnTheLeft,
            activeOnTheRight: this.options.activeOnTheRight,
        };
    }
}

class MarkVisibleAndActiveResult {
    // Viewport range is subarray of slides which are
    // currently in viewport
    constructor(slides, inViewportRangeStart, inViewportRangeEnd, slidesModified) {
        this.slides = slides;
        this.inViewportRangeStart = inViewportRangeStart;
        this.inViewportRangeEnd = inViewportRangeEnd;
        this.slidesModified = slidesModified;
    }
}

/**
 * Returns cloned slides with modified `inViewport` and `isActive` fields
 */
function markVisibleAndActive(slides, offset, slideWidth, viewportWidth, activeSlideIndex, threshold, alignMode) {
    if (!slides || !slides.length) {
        return new MarkVisibleAndActiveResult([], 0, 0, false);
    }
    const newSlides = [];
    /** Slide index representing first slide inside viewport */
    let inViewportStart = null;
    /** Slide index representing last slide inside viewport */
    let inViewportEnd = null;
    let slidesModified = false;
    for (let i = 0, currentOffset = offset; i < slides.length; i++, currentOffset += slideWidth) {
        // Calculate slide options
        const slideBeforeViewportEnd = currentOffset < viewportWidth + threshold;
        const slideAfterViewportStart = currentOffset + slideWidth + threshold > 0;
        const inViewport = slideBeforeViewportEnd && slideAfterViewportStart;
        const options = {
            inViewport,
            item: slides[i].options.item,
            isActive: i === activeSlideIndex,
            activeOnTheRight: i < activeSlideIndex,
            activeOnTheLeft: i > activeSlideIndex,
        };
        const context = slides[i].context;
        slidesModified = slidesModified
            || context.inViewport !== inViewport
            || context.isActive !== options.isActive
            || context.activeOnTheLeft !== options.activeOnTheLeft
            || context.activeOnTheRight !== options.activeOnTheRight;
        // Construct new slide
        const newSlide = new CarouselSlide(slides[i].id, slides[i].itemIndex, options);
        newSlides.push(newSlide);
        // Detect viewport range
        if (inViewport && inViewportStart === null) {
            inViewportStart = i;
        }
        else if (!inViewport && inViewportStart !== null && inViewportEnd === null) {
            inViewportEnd = Math.max(0, i - 1);
        }
    }
    // Edge case when last slide was in viewport:
    // in such case viewport end index won't be assigned
    // inside slide cycle due to loop algorithm specifics
    if (inViewportStart !== null && inViewportEnd === null) {
        inViewportEnd = slides.length - 1;
    }
    if (activeSlideIndex >= 0 && activeSlideIndex <= slides.length - 1) { // Active slide might be outside slide range
        if (inViewportStart === null || activeSlideIndex <= inViewportStart) {
            const distanceToActiveSlideViewportStart = alignMode === CarouselAlignMode.LEFT
                ? threshold
                : viewportWidth / 2 - slideWidth / 2 + threshold;
            const slidesToViewportStart = Math.ceil(distanceToActiveSlideViewportStart / slideWidth);
            inViewportStart = Math.max(0, activeSlideIndex - slidesToViewportStart);
        }
        if (inViewportEnd === null || activeSlideIndex >= inViewportEnd) {
            const distanceToActiveSlideViewportEnd = alignMode === CarouselAlignMode.LEFT
                ? viewportWidth + threshold
                : viewportWidth / 2 + slideWidth / 2 + threshold;
            const slidesToViewportEnd = Math.ceil(distanceToActiveSlideViewportEnd / slideWidth);
            inViewportEnd = Math.min(slides.length - 1, activeSlideIndex + slidesToViewportEnd);
        }
    }
    const result = new MarkVisibleAndActiveResult(newSlides, inViewportStart || 0, inViewportEnd || 0, slidesModified);
    return result;
}

/**
 * Assigns inViewport and isActive option to each slide
 */
function markVisibleAndActiveProcedure() {
    return ({ state, procedureState }) => {
        const result = markVisibleAndActive(state.slides, state.offset, state.config.slideWidth, getViewportWidth(state), state.activeSlideIndex, state.config.threshold, state.config.alignMode);
        const modifiedState = {
            ...state,
            slides: result.slides,
            slideIndex: state.slideIndex + (result.slidesModified ? 1 : 0),
            activeItemIndex: result.slides[state.activeSlideIndex]?.itemIndex ?? 0, // 0 when no slides available
        };
        const modifiedProcedureState = {
            ...procedureState,
            inViewportRange: [result.inViewportRangeStart, result.inViewportRangeEnd],
        };
        return new ContinueWith(modifiedState, modifiedProcedureState);
    };
}

class RemoveExcessiveResult {
    constructor(slides, offset, activeSlideIndex, activeItemIndex, slidesChanged) {
        this.slides = slides;
        this.offset = offset;
        this.activeSlideIndex = activeSlideIndex;
        this.activeItemIndex = activeItemIndex;
        this.slidesChanged = slidesChanged;
    }
}

/**
 * Removes slides that should not exist in carousel
 * (e.g. copies which are outside viewport)
 */
function removeExcessive(slides, offset, slideWidth, activeSlideIndex, 
/** First slide index that is inside viewport */
viewportStart, 
/** Last slide index that is inside viewport */
viewportEnd) {
    if (!slides || !slides.length || viewportStart === null || viewportEnd === null) {
        return new RemoveExcessiveResult([], offset, 0, 0, false);
    }
    // Validate inputs
    viewportStart = Math.max(0, Math.min(viewportStart, viewportEnd));
    viewportEnd = Math.min(viewportEnd, slides.length - 1);
    const rightSlides = [];
    const leftSlides = [];
    const itemIndexes = new Set();
    let newOffset = offset;
    // Next fields should help us to detect new slide index
    let leftActiveSlideIndex = -1;
    let rightActiveSlideIndex = -1;
    let removedSlidesFromLeftSide = 0;
    let removedSlidesFromRightSide = 0;
    // Next fancy loop traverses through slides array, but in specified order:
    // first we travel through slides in viewport, so we can collect item ids,
    // next we go through right and left side outside viewport, cleaning all
    // found copies. Thus we can cleanse all copies in O(n)
    for (let i = viewportStart; i <= viewportEnd; i++) { // viewport traverse
        itemIndexes.add(slides[i].itemIndex);
        rightSlides.push(slides[i]);
        if (slides[i].options.isActive) {
            rightActiveSlideIndex = rightSlides.length - 1;
        }
    }
    for (let i = viewportEnd + 1; i < slides.length; i++) { // right side traverse
        if (itemIndexes.has(slides[i].itemIndex)) {
            removedSlidesFromRightSide++;
            continue;
        }
        itemIndexes.add(slides[i].itemIndex);
        rightSlides.push(slides[i]);
        if (slides[i].options.isActive) {
            rightActiveSlideIndex = rightSlides.length - 1;
        }
    }
    for (let i = 0; i < viewportStart; i++) { // left side traverse
        if (itemIndexes.has(slides[i].itemIndex)) {
            newOffset += slideWidth;
            removedSlidesFromLeftSide++;
            continue;
        }
        itemIndexes.add(slides[i].itemIndex);
        leftSlides.push(slides[i]);
        if (slides[i].options.isActive) {
            leftActiveSlideIndex = i;
        }
    }
    if (rightActiveSlideIndex !== -1) {
        activeSlideIndex = rightActiveSlideIndex + leftSlides.length;
    }
    const newSlides = [
        ...leftSlides,
        ...rightSlides,
    ];
    const newActiveSlideIndex = rightActiveSlideIndex !== -1
        ? rightActiveSlideIndex + leftSlides.length
        : leftActiveSlideIndex !== -1
            ? leftActiveSlideIndex
            : activeSlideIndex - removedSlidesFromLeftSide;
    const activeItemIndex = newSlides[newActiveSlideIndex].itemIndex;
    const slidesChanged = Boolean(removedSlidesFromLeftSide || removedSlidesFromRightSide || newActiveSlideIndex !== activeSlideIndex);
    return new RemoveExcessiveResult(newSlides, newOffset, newActiveSlideIndex, activeItemIndex, slidesChanged);
}

/**
 * Removes slide copies
 */
function removeExcessiveProcedure() {
    return ({ state, procedureState }) => {
        const result = removeExcessive(state.slides, state.offset, state.config.slideWidth, state.activeSlideIndex, procedureState?.inViewportRange?.[0] ?? null, procedureState?.inViewportRange?.[1] ?? null);
        const modifiedState = {
            ...state,
            slides: result.slides,
            offset: result.offset,
            activeSlideIndex: result.activeSlideIndex,
            activeItemIndex: result.activeItemIndex,
            slideIndex: state.slideIndex + (result.slidesChanged ? 1 : 0),
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Result for helper function `moveOrCopySlidesTo`
 */
class CopySlidesResult {
    constructor(slides = [], modifiedOffset = 0, slidesChanged = false) {
        this.slides = slides;
        this.modifiedOffset = modifiedOffset;
        this.slidesChanged = slidesChanged;
    }
}

/**
 * Result for helper function `shuffleSlides`
 */
class ShuffleSlidesResult {
    constructor(slides = [], modifiedOffset = 0, slidesChanged = false) {
        this.slides = slides;
        this.modifiedOffset = modifiedOffset;
        this.slidesChanged = slidesChanged;
    }
}

/**
 * Justifies existing slides in viewport by either
 * - Move slide from one side to another
 * - Copy slide from one side to another
 * - Create slide on one side
 *
 * Task of this function is to leave no empty spaces in viewport.
 *
 * **BE ADVISED**, that inViewport flag should be calculated for each slide
 * that should not be moved beforehand.
 *
 * **BE ADVISED**, that slides should be marked with active flag afterwards,
 * since shuffle might break actual order and does not care where the active
 * slide is.
 */
function shuffleSlides(slides, offset, slideWidth, viewportWidth, items, shouldLoop, threshold = 0, idGenerator = new IdGenerator()) {
    if (!slides || !slides.length) {
        return new ShuffleSlidesResult([], offset, false);
    }
    if (slideWidth <= 0 || !shouldLoop) {
        return new ShuffleSlidesResult(slides, offset, false);
    }
    let shuffledSlides = slides;
    let slidesChanged = false;
    const slideSumWidth = slides.length * slideWidth;
    // Calculate missing slides for left and right sides
    const leftSideMissingSlides = Math.max(0, Math.ceil((offset + threshold) / slideWidth));
    const rightSideMissingSlides = Math.max(0, Math.ceil((viewportWidth + threshold - (offset + slideSumWidth)) / slideWidth));
    // Let's start to fill missing slides
    // Should move slides to right side
    if (rightSideMissingSlides) {
        const rightSideMoveResult = moveOrCopySlidesToEnd(shuffledSlides, offset, rightSideMissingSlides, slideWidth, items, idGenerator);
        shuffledSlides = rightSideMoveResult.slides;
        offset = rightSideMoveResult.modifiedOffset;
        slidesChanged = slidesChanged || rightSideMoveResult.slidesChanged;
    }
    // Should move slides to left side
    if (leftSideMissingSlides) {
        const leftSideMoveResult = moveOrCopySlidesToStart(shuffledSlides, offset, leftSideMissingSlides, slideWidth, items, idGenerator);
        shuffledSlides = leftSideMoveResult.slides;
        offset = leftSideMoveResult.modifiedOffset;
        slidesChanged = slidesChanged || leftSideMoveResult.slidesChanged;
    }
    const result = new ShuffleSlidesResult(shuffledSlides, offset, slidesChanged);
    return result;
}
/**
 * Moves, copies or creates slides to the right side in order to fill
 * desired `quantity`.
 *
 * Quantity means of how much new slides will be added.
 * There are two options of how we could fill missing slides.
 * Imagine next slides row mapped to their item index:
 * [3, 4, 5, 0, 1, 2, 3, 4, 5] and lets take `quantity` = 4.
 * We can't just start to move/copy slides from the right part,
 * because we can't break sequence (meaning if we'll copy slide `5`
 * on the right to slide `3` on the left, they won't stack).
 * Slide `3` on the left awaits slide with item index `2` to be put
 * near him, so we should create some slides in between. This action
 * is named `Option FILL GAP`.
 *
 * `Option FILL GAP`:
 * How much slides are in between item index `5` and item index `3`?
 * Answer is 3: [**5**, 0, 1, 2, **3**]. So let's fill those 3 slides
 * and subtract their length from `quantity`.
 * Now we have row [0, 1, 2, 3, 4, 5, 0, 1, 2, 3, 4, 5] and `quantity`
 * equals 1. Now we can start copy/move slides from the right part.
 * This action is called `Option COPY`
 *
 * `Option COPY`:
 * When meddling with slides from the other side, we have two options
 * of what to do with them: either copy or move. Slide can be moved
 * when it is not `inViewport`, meaning it has corresponding option
 * flag. Otherwise it should be copied.
 * In our example we should move one slide from the right part, since
 * `quantity` after `Option FILL` equals 1. Lets imagine that slide
 * wasn't in viewport and now our slide row has
 * [5, 0, 1, 2, 3, 4, 5, 0, 1, 2, 3, 4], voila!
 */
function moveOrCopySlidesToEnd(slides, offset, quantity, slideWidth, items, idGenerator = new IdGenerator()) {
    if (quantity < 1) {
        return new CopySlidesResult(slides, offset, false);
    }
    slides = [...slides]; // Shallow copy, since we're going to mutate them;
    const newSlides = [];
    /** Whether we're modifying slides array */
    let slidesChanged = false;
    /** Used as argument for splice call later */
    let spliceQuantity = 0;
    /**
     * We can't increase quantity if we encountered slide
     * with inViewport flag
     */
    let canIncreaseQuantity = true;
    /**
     * Iterator index, used in COPY option. Determines which
     * slide should be picked from left side for analyzing.
     */
    let slideIndex = 0;
    /**
     * Once we finished with option FILL GAP, then
     * copy option should take place
     */
    let shouldCopy = false;
    // We going to iterate through item indexes starting from
    // last slide item index. Let's pick an item index
    // to start from.
    const lastSlide = slides[slides.length - 1];
    const lastSlideHasLastItemIndex = lastSlide.itemIndex >= items.length - 1;
    /**
     * Iterator index, used in FILL GAP and COPY options.
     * This index determines which item will next slide have.
     */
    let nextItemId = lastSlideHasLastItemIndex
        ? 0
        : lastSlide.itemIndex + 1;
    for (let i = 0; i < quantity; i++) {
        /**
         * Having this field as true means slides from left and right
         * side (including new slides) can stack and we can proceed to
         * COPY option
         */
        const firstSlideHasNextItemId = slides[0].itemIndex === nextItemId;
        if (shouldCopy || firstSlideHasNextItemId) {
            // Option COPY
            shouldCopy = true;
            const currentSlide = slides[slideIndex];
            // Create new slide procedure
            const newSlide = new CarouselSlide(idGenerator.next(), nextItemId, {
                item: currentSlide.options.item,
                // Every filled item is considered to be in viewport,
                // because why else would we call this function
                // otherwise? To fill viewport obviously.
                inViewport: true,
                // Shuffling does not care about active slides,
                // use default values
                isActive: false,
                activeOnTheLeft: false,
                activeOnTheRight: false,
            });
            newSlides.push(newSlide);
            slidesChanged = true;
            // Splice arguments processing
            if (currentSlide?.options?.inViewport) {
                canIncreaseQuantity = false;
            }
            // We can't increase quantity if we have reached end of slides.
            // It happens when quantity from argument is higher than
            // slides size.
            const canIncrementSpliceQuantity = spliceQuantity + 1 <= slides.length;
            if (canIncreaseQuantity && canIncrementSpliceQuantity) {
                spliceQuantity++;
            }
            // Prepare slide to process
            slideIndex++;
            if (slideIndex >= slides.length) {
                slideIndex = 0;
            }
        }
        else {
            // Option FILL GAP
            // Create new slide procedure
            const newSlide = new CarouselSlide(idGenerator.next(), nextItemId, {
                // Every filled item is considered to be in viewport,
                // because why else would we call this function
                // otherwise? To fill viewport obviously.
                inViewport: true,
                item: items[nextItemId],
                // Shuffling does not care about active slides,
                // use default values
                isActive: false,
                activeOnTheLeft: false,
                activeOnTheRight: false,
            });
            newSlides.push(newSlide);
            slidesChanged = true;
        }
        // Pick index for next item
        nextItemId++;
        if (nextItemId >= items.length) {
            nextItemId = 0;
        }
    }
    if (spliceQuantity > 0) {
        slides.splice(0, spliceQuantity);
        slidesChanged = true;
    }
    const resultSlides = [
        ...slides,
        ...newSlides,
    ];
    const resultOffset = offset + spliceQuantity * slideWidth;
    return new CopySlidesResult(resultSlides, resultOffset, slidesChanged);
}
/**
 * Moves, copies or creates slides to the left side in order to fill
 * desired `quantity`.
 *
 * Quantity means of how much new slides will be added.
 * There are two options of how we could fill missing slides.
 * Imagine next slides row mapped to their item index:
 * [0, 1, 2, 3, 4, 5, 0, 1, 2, 3] and lets take `quantity` = 4.
 * We can't just start to move/copy slides from the left part,
 * because we can't break sequence (meaning if we'll copy slide `0`
 * on the right to slide `3` on the right, they won't stack).
 * Slide `3` on the right side awaits slide with item index `2` to be
 * put near him, so we should create some slides in between. This
 * action is named `Option FILL GAP`.
 *
 * `Option FILL GAP`:
 * How much slides are in between item index `0` and item index `3`?
 * Answer is 2: [**3**, 4, 5, **0**]. So let's fill these 2 slides
 * and subtract their length from `quantity`.
 * Now we have row [0, 1, 2, 3, 4, 5, 0, 1, 2, 3, 4, 5] and `quantity`
 * equals 2. Now we can start copy/move slides from the left part.
 * This action is called `Option COPY`
 *
 * `Option COPY`:
 * When meddling with slides from the other side, we have two options
 * of what to do with them: either copy or move. Slide can be moved
 * when it is not `inViewport`, meaning it has corresponding option
 * flag. Otherwise it should be copied.
 * In our example we should move two slides from the left part, since
 * `quantity` after `Option FILL` equals 2. Lets imagine that slides
 * weren't in viewport and now our row has
 * [2, 3, 4, 5, 0, 1, 2, 3, 4, 5, 0, 1], voila!
 */
function moveOrCopySlidesToStart(slides, offset, quantity, slideWidth, items, idGenerator = new IdGenerator()) {
    if (quantity < 1) {
        return new CopySlidesResult(slides, offset);
    }
    slides = [...slides]; // Shallow copy, since we're going to mutate them;
    const newSlides = [];
    /** Whether we're modifying slides array */
    let slidesChanged = false;
    /** Used as argument for splice call later */
    let spliceFrom = null;
    /** Used as argument for splice call later */
    let spliceQuantity = 0;
    /**
     * Once we finished with option FILL GAP, then
     * copy option should take place
     */
    let shouldCopy = false;
    /**
     * Iterator index, used in COPY option. Determines which
     * slide should be picked from right side for analyzing.
     */
    let slideIndex = slides.length - 1;
    // We going to iterate through item indexes starting from
    // first slide item index. Let's pick an item index
    // to start from.
    const firstSlide = slides[0];
    const firstSlideHasFirstItemId = firstSlide.itemIndex === 0;
    /**
     * Iterator index, used in FILL GAP and COPY options.
     * This index determines which item will next slide have.
     */
    let nextItemId = firstSlideHasFirstItemId
        ? items.length - 1
        : firstSlide.itemIndex - 1;
    for (let i = 0; i < quantity; i++) {
        /**
         * Having this field as true means slides from left and right
         * side (including new slides) can stack and we can proceed to
         * COPY option
         */
        const lastSlideHasNextItemId = slides[slides.length - 1].itemIndex === nextItemId;
        if (shouldCopy || lastSlideHasNextItemId) {
            // Option COPY
            shouldCopy = true;
            const currentSlide = slides[slideIndex];
            // Slide copy procedure
            const newSlide = new CarouselSlide(idGenerator.next(), nextItemId, {
                item: currentSlide.options.item,
                // Every filled item is considered to be in viewport,
                // because why else would we call this function
                // otherwise? To fill viewport obviously.
                inViewport: true,
                // Shuffling does not care about active slides,
                // use default values
                isActive: false,
                activeOnTheLeft: false,
                activeOnTheRight: false,
            });
            newSlides.push(newSlide);
            slidesChanged = true;
            // Prepare next slide index
            slideIndex--;
            if (slideIndex < 0) {
                slideIndex = slides.length - 1;
            }
            // Splice arguments processing
            // spliceFrom can be assigned once and equals first inViewport
            // slide that we met
            if (spliceFrom === null && currentSlide?.options?.inViewport) {
                spliceFrom = slides.length - spliceQuantity;
            }
            // We can't increase quantity if we have determined
            // from where we should splice slides
            const slideCopyNotEncountered = spliceFrom === null;
            // We can't have splice quantity higher than slides size.
            // It happens when quantity from argument is higher than
            // slides size.
            const canIncrementSpliceQuantity = spliceQuantity + 1 <= slides.length;
            if (slideCopyNotEncountered && canIncrementSpliceQuantity) {
                spliceQuantity++;
            }
        }
        else {
            // Option FILL GAP
            // Slide copy procedure
            const newSlide = new CarouselSlide(idGenerator.next(), nextItemId, {
                // Every filled item is considered to be in viewport,
                // because why else would we call this function
                // otherwise? To fill viewport obviously.
                inViewport: true,
                item: items[nextItemId],
                // Shuffling does not care about active slides,
                // use default values
                isActive: false,
                activeOnTheLeft: false,
                activeOnTheRight: false,
            });
            newSlides.push(newSlide);
            slidesChanged = true;
        }
        // Pick index for next item
        nextItemId--;
        if (nextItemId < 0) {
            nextItemId = items.length - 1;
        }
    }
    if (spliceQuantity > 0) {
        spliceFrom = spliceFrom === null
            ? slides.length - spliceQuantity
            : spliceFrom;
        slides.splice(spliceFrom, spliceQuantity);
        slidesChanged = true;
    }
    const result = [
        ...newSlides.reverse(),
        ...slides,
    ];
    return new CopySlidesResult(result, offset - newSlides.length * slideWidth, slidesChanged);
}

/**
 * Moves slide from left side to right or vise versa
 * in order to balance weight or predict offset changes
 */
function shuffleSlidesProcedure() {
    return ({ state, procedureState, environment }) => {
        const result = shuffleSlides(state.slides, state.offset, state.config.slideWidth, getViewportWidth(state), state.config.items, state.config.shouldLoop, state.config.threshold, environment?.slideIdGenerator ?? new IdGenerator());
        const modifiedState = {
            ...state,
            slides: result.slides,
            activeSlideIndex: result.slides.findIndex((item) => item.options.isActive) || 0,
            offset: result.modifiedOffset,
            slideIndex: state.slideIndex + (result.slidesChanged ? 1 : 0),
        };
        // Might be necessary for animations
        const modifiedProcedureState = {
            ...procedureState,
            offsetSnapshot: result.modifiedOffset - state.offset + (procedureState?.offsetSnapshot ?? 0),
        };
        return new ContinueWith(modifiedState, modifiedProcedureState);
    };
}

/**
 * Cleans state from excessive slides and completes
 * corresponding duties after completed transition
 */
function cleanupProcedure() {
    return procedurePipe('cleanup', destroyAnimationProcedure(), markVisibleAndActiveProcedure(), calculateOffsetProcedure(), removeExcessiveProcedure(), shuffleSlidesProcedure(), markVisibleAndActiveProcedure());
}

/**
 * Result for helper function `calculateActiveSlide`
 */
class CalculateActiveSlideResult {
    constructor(modifiedOffset = 0, slideIndex = 0) {
        this.modifiedOffset = modifiedOffset;
        this.slideIndex = slideIndex;
    }
}

/**
 * Based on current whereabouts, returns most possible
 * active slide candidate and proposed offset for it
 */
function calculateActiveSlide(slides, offset, alignMode, slideWidth, viewportWidth, swipeThresholdPercent, 
/** Distance (whether in px or %) that is aligned to carousel width mode */
swipeDistance = null) {
    const result = new CalculateActiveSlideResult(offset, 0);
    // Noop run if nothing to calculate
    if (!slides.length || slideWidth <= 0) {
        return result;
    }
    const slidesSumWidth = slideWidth * slides.length;
    // By given align mode, width mode and viewport width, calculate
    // carousel center position
    const carouselCenter = alignMode === CarouselAlignMode.LEFT
        ? 0
        : viewportWidth / 2;
    // Slide center is not always its left side. On such occasion we should
    // correct its offset using specified align mode.
    const slideRightAmendment = alignMode === CarouselAlignMode.LEFT
        ? slideWidth
        : slideWidth / 2;
    const slideLeftAmendment = alignMode === CarouselAlignMode.LEFT
        ? 0
        : slideRightAmendment;
    // Preset if slides far behind carousel center
    if (offset + slidesSumWidth < carouselCenter) {
        result.slideIndex = slides.length - 1;
        result.modifiedOffset = carouselCenter - slidesSumWidth + slideRightAmendment;
        // Preset if slides far away from carousel center
    }
    else if (offset - slideLeftAmendment > carouselCenter) {
        result.slideIndex = 0;
        result.modifiedOffset = carouselCenter - slideLeftAmendment;
        // Any other cases (when slides intersect carousel center)
    }
    else {
        result.slideIndex = Math.floor(Math.abs(carouselCenter - offset) / slideWidth);
        result.modifiedOffset = carouselCenter - result.slideIndex * slideWidth - slideLeftAmendment;
    }
    // Swipe correction: animation must align with swipe direction meaning
    // when user swipes right, final animation should also lead to the right
    const swipeDirection = Math.sign(swipeDistance ?? 0);
    const offsetDirection = offset > result.modifiedOffset
        ? -1
        : 1;
    const shouldApplySwipeAlignment = swipeDistance !== null
        && swipeThresholdPercent !== null
        && Math.abs(swipeDistance) > Math.abs(swipeThresholdPercent)
        && swipeDirection !== offsetDirection;
    if (shouldApplySwipeAlignment) {
        const appliedSwipeAlignment = result.slideIndex - swipeDirection;
        const newSlideIndex = Math.min(Math.max(0, appliedSwipeAlignment), slides.length - 1);
        if (newSlideIndex !== result.slideIndex) {
            result.slideIndex = newSlideIndex;
            result.modifiedOffset += swipeDirection * slideWidth;
        }
    }
    return result;
}

/**
 * Given current slide state, assigns active slide
 */
function calculateActiveSlideProcedure() {
    return ({ state, procedureState, environment }) => {
        const result = calculateActiveSlide(state.slides, state.offset, state.config.alignMode, state.config.slideWidth, getViewportWidth(state), Math.min(state.config.slideWidth / 2, environment?.swipeThreshold ?? Infinity), procedureState.passedDistance || null);
        const modifiedState = {
            ...state,
            activeSlideIndex: result.slideIndex,
            activeItemIndex: state.slides[result.slideIndex]?.itemIndex ?? 0,
        };
        return new ContinueWith(modifiedState);
    };
}

/** Converts passed distance to carousel width units */
function normalizePassedDistanceProcedure(passedDistance) {
    return ({ state, procedureState }) => {
        const modifiedProcedureState = {
            ...procedureState,
            passedDistance: state.config.widthMode === CarouselWidthMode.PERCENT
                ? 100 * passedDistance / getViewportWidthInPx(state)
                : passedDistance,
        };
        return new ContinueWith(state, modifiedProcedureState);
    };
}

/**
 * Calculates offset for animation specific tick
 */
function animationOffsetSnapshot(currentPosition, totalDuration, from, to, offsetFallback, bezierFn) {
    if (from === null || to === null) {
        return offsetFallback;
    }
    if (totalDuration <= 0 || currentPosition < 0 || to === from) {
        return to;
    }
    if (!bezierFn) {
        return from;
    }
    const animationDistance = Math.abs(to - from);
    const completedDistancePercent = currentPosition / totalDuration;
    const completedDistance = animationDistance * bezierFn(completedDistancePercent);
    const offsetSnapshot = to > from
        ? from + completedDistance
        : from - completedDistance;
    return offsetSnapshot;
}

/**
 * Assigns offset to state like if animation was paused.
 * If animation is not available, current offset will be assigned.
 */
function animationOffsetSnapshotProcedure() {
    return ({ state }) => {
        const time = new Date().getTime();
        const currentPosition = time - (state.animation?.startTime ?? time);
        const offset = animationOffsetSnapshot(currentPosition, state.config.transitionDuration, state.animation?.from ?? null, state.animation?.to ?? null, state.offset, state.animationBezierFn);
        const modifiedState = {
            ...state,
            offset,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Animation state that is currently in process
 */
class CarouselAnimation {
    constructor(from, to, player, startTime = new Date().getTime()) {
        this.from = from;
        this.to = to;
        this.player = player;
        this.startTime = startTime;
    }
}

function startAnimation(container, from, to, widthMode, transitionDuration, bezierArgs, isBrowser, afterAnimationAction) {
    if (!isBrowser || !container || from === null) {
        return null;
    }
    const cubicBezier = `cubic-bezier(${bezierArgs[0]},${bezierArgs[1]},${bezierArgs[2]},${bezierArgs[3]})`;
    const animationNative = container?.animate?.([
        { transform: `translateX(${from}${widthMode})` },
        { transform: `translateX(${to}${widthMode})` },
    ], {
        duration: transitionDuration,
        easing: cubicBezier,
    });
    if (animationNative) {
        animationNative.onfinish = () => {
            afterAnimationAction();
        };
    }
    const animation = new CarouselAnimation(from, to, animationNative);
    animationNative?.play?.();
    return animation;
}

/**
 * Creates new transition from one slide to another
 */
function startAnimationProcedure() {
    return ({ state, procedureState, environment }) => {
        const animation = startAnimation(state.animatableContainer, procedureState?.offsetSnapshot ?? null, state.offset, state.config.widthMode, state.config.transitionDuration, environment?.animationBezierArgs ?? [], environment?.isBrowser ?? false, environment?.afterAnimationAction ?? (() => { }));
        const modifiedState = {
            ...state,
            animation,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Save offset to procedure state until later use
 */
function setOffsetSnapshotProcedure() {
    return ({ state, procedureState }) => {
        const modifiedProcedureState = {
            ...procedureState,
            offsetSnapshot: state.offset,
        };
        return new ContinueWith(state, modifiedProcedureState);
    };
}

/**
 * Calculates corresponding parts for current active slide
 */
function setSlideIndexProcedure() {
    return procedurePipe('setSlideIndex', animationOffsetSnapshotProcedure(), destroyAnimationProcedure(), setOffsetSnapshotProcedure(), markVisibleAndActiveProcedure(), calculateOffsetProcedure(), shuffleSlidesProcedure(), markVisibleAndActiveProcedure(), startAnimationProcedure());
}

/**
 * Calculates state after user finish drag event
 */
function dragEndProcedure(passedDistance) {
    return procedurePipe('dragEnd', enableAutoplayProcedure("drag" /* AutoplaySuspender.DRAG */), normalizePassedDistanceProcedure(passedDistance), calculateActiveSlideProcedure(), setSlideIndexProcedure());
}

/**
 * Return new offset modified by last drag operations.
 * Applies overscroll behavior when necessary.
 *
 * @returns modified offset
 */
function dragOffsetSnapshot(
/** X position in pixels from where drag event began */
fromX, 
/** destination X in pixels of last fired drag event */
toX, currentOffset, widthMode, alignMode, shouldLoop, viewportWidth, viewportWidthInPx, slideWidth, slideSumWidth, 
/** How much in percents user can overscroll unlooped carousel */
maxOverscrollPercent, bezierFn, invertedBezierFn) {
    if (fromX === toX) {
        return currentOffset;
    }
    if (maxOverscrollPercent === null) {
        return currentOffset;
    }
    if (!bezierFn || !invertedBezierFn) {
        return currentOffset;
    }
    // Normalize all pixel values to current width mode
    if (widthMode === CarouselWidthMode.PERCENT) {
        fromX = fromX * 100 / viewportWidthInPx;
        toX = toX * 100 / viewportWidthInPx;
    }
    let offsetDelta = toX - fromX;
    // 1st case: no bezier amendments, just return offset with applied delta
    if (shouldLoop) {
        return currentOffset + offsetDelta;
    }
    const overscrollStartPoint = detectOverscrollStartPoint(fromX, currentOffset, viewportWidth, slideWidth, slideSumWidth, alignMode, maxOverscrollPercent, invertedBezierFn);
    // 2nd case where bezier correction haven't started yet,
    // meaning we're in safe zone at both side
    if (overscrollStartPoint === null) {
        const offsetFromSafeZone = applyDeltaFromSafeZone(currentOffset, offsetDelta, alignMode, viewportWidth, slideWidth, slideSumWidth, maxOverscrollPercent, bezierFn);
        return offsetFromSafeZone;
    }
    // Seems like we already at overscroll zone, so let's see
    // the ways we could manage that
    /** Whether drag was performed from overscroll zone to same overscroll zone */
    const dragStaysInSameOverscrollZone = fromX > overscrollStartPoint
        ? toX > overscrollStartPoint
        : toX < overscrollStartPoint;
    // 3rd case: we should not amend when drag pointer is still in overscroll zone
    // after applying delta
    if (dragStaysInSameOverscrollZone) {
        const distanceBefore = fromX - overscrollStartPoint;
        const distanceAfter = toX - overscrollStartPoint;
        const distance = distanceBetween(distanceBefore, distanceAfter, viewportWidth, maxOverscrollPercent, bezierFn);
        currentOffset = currentOffset - distance;
        return currentOffset;
    }
    // 4th case where drag pointer has returned to safe zone.
    // We should do the same calculation like we did in 2nd case.
    const overscrollDistance = applyDeltaFromOverscrollZone(overscrollStartPoint - fromX, viewportWidth, maxOverscrollPercent, bezierFn);
    const distancetoSafeZone = overscrollStartPoint - fromX;
    currentOffset += overscrollDistance;
    fromX = overscrollStartPoint;
    offsetDelta -= distancetoSafeZone;
    const result = applyDeltaFromSafeZone(currentOffset, offsetDelta, alignMode, viewportWidth, slideWidth, slideSumWidth, maxOverscrollPercent, bezierFn);
    return result;
}
/**
 * Applies bezier on distance and limit with max overscroll distance
 *
 * BE ADVISED, that all argument values should be converted to current width mode
 * beforehand.
 */
function applyDeltaFromOverscrollZone(distance, viewportWidth, maxOverscrollPercent, bezierFn) {
    /** Viewport % from 0 to 1 of delta distance */
    const deltaPercentage = Math.max(0, Math.min(Math.abs(distance) / viewportWidth, 1));
    /** Maximal distance of overscroll in pixels */
    const maxOverscrollDistance = viewportWidth * maxOverscrollPercent / 100;
    /**
     * How much delta is actually long when applying bezier
     * and aligning overscroll max distance
     */
    const deltaDistance = maxOverscrollDistance * bezierFn(deltaPercentage) * Math.sign(distance);
    return deltaDistance;
}
/**
 * Applies delta to current offset which is currently in safe zone at this moment.
 * Applies bezier whether delta intersects overscroll zone.
 *
 * BE ADVISED, that all argument values should be converted to current width mode
 * beforehand.
 */
function applyDeltaFromSafeZone(currentOffset, offsetDelta, alignMode, viewportWidth, slideWidth, slideSumWidth, maxOverscrollPercent, bezierFn) {
    const leftmostPoint = alignMode === CarouselAlignMode.CENTER
        ? viewportWidth / 2 - slideWidth / 2
        : 0;
    const rightmostPoint = alignMode === CarouselAlignMode.CENTER
        ? viewportWidth / 2 + slideWidth / 2
        : Math.min(viewportWidth, slideSumWidth);
    /** Whether left side of carousel is in viewport after applying delta */
    const leftSideExposedAfter = currentOffset + offsetDelta > leftmostPoint;
    /** Whether right side of carousel is in viewport after applying delta */
    const rightSideExposedAfter = currentOffset + slideSumWidth + offsetDelta < rightmostPoint;
    const canSafelyTransit = (!leftSideExposedAfter && offsetDelta > 0)
        || (!rightSideExposedAfter && offsetDelta < 0);
    if (canSafelyTransit) {
        // We're still safe on both sides, just apply delta
        return currentOffset + offsetDelta;
    }
    /**
     * Distance between current offset and point
     * where overscroll begins
     */
    let distanceToSafeZone = 0;
    // Move offset to the point where overscroll begins
    if (leftSideExposedAfter) {
        distanceToSafeZone = currentOffset - leftmostPoint;
    }
    else if (rightSideExposedAfter) {
        distanceToSafeZone = currentOffset + slideSumWidth - rightmostPoint;
    }
    /**
     * Distance between current offset and
     * overscroll zone starting point
     */
    const distanceWithoutSafeZone = offsetDelta + distanceToSafeZone;
    currentOffset -= distanceToSafeZone;
    const overscrollDelta = applyDeltaFromOverscrollZone(distanceWithoutSafeZone, viewportWidth, maxOverscrollPercent, bezierFn);
    currentOffset += overscrollDelta;
    return currentOffset;
}
/**
 * Returns point where overscroll should potentially start
 * or null if carousel is currently in safe zone.
 */
function detectOverscrollStartPoint(fromX, currentOffset, viewportWidth, slideWidth, slideSumWidth, alignMode, maxOverscrollPercent, invertedBezierFn) {
    const leftmostPoint = alignMode === CarouselAlignMode.CENTER
        ? viewportWidth / 2 - slideWidth / 2
        : 0;
    const rightmostPoint = alignMode === CarouselAlignMode.CENTER
        ? viewportWidth / 2 + slideWidth / 2
        : Math.min(leftmostPoint + slideSumWidth, viewportWidth);
    /** Distance from current offset to safe zone */
    let overscrollConvertedDistance = null;
    // Special case when left-aligned carousel is lesser than viewport width
    if (alignMode === CarouselAlignMode.LEFT && slideSumWidth < viewportWidth && currentOffset !== 0) {
        overscrollConvertedDistance = currentOffset;
    }
    else if (currentOffset > leftmostPoint) {
        overscrollConvertedDistance = currentOffset - leftmostPoint;
    }
    else if (currentOffset < rightmostPoint - slideSumWidth) {
        overscrollConvertedDistance = currentOffset - rightmostPoint + slideSumWidth;
    }
    if (overscrollConvertedDistance === null) {
        return null;
    }
    const overscrollRealDistance = extractDeltaFromOverscrollZone(overscrollConvertedDistance, viewportWidth, maxOverscrollPercent, invertedBezierFn);
    const overscrollStartPoint = fromX - overscrollRealDistance;
    return overscrollStartPoint;
}
/**
 * Converts distance with bezier applied to distance
 * without bezier
 */
function extractDeltaFromOverscrollZone(convertedDistance, viewportWidth, maxOverscrollPercent, invertedBezierFn) {
    const maxOverscrollDistance = viewportWidth * maxOverscrollPercent / 100;
    const bezierValue = Math.abs(convertedDistance / maxOverscrollDistance);
    const deltaPercentage = invertedBezierFn(bezierValue) * viewportWidth * Math.sign(convertedDistance);
    return deltaPercentage;
}
/**
 * Calculates overscroll distance between two points
 */
function distanceBetween(firstPoint, secondPoint, viewportWidth, maxOverscrollPercent, bezierFn) {
    const overscrollBefore = applyDeltaFromOverscrollZone(firstPoint, viewportWidth, maxOverscrollPercent, bezierFn);
    const overscrollAfter = applyDeltaFromOverscrollZone(secondPoint, viewportWidth, maxOverscrollPercent, bezierFn);
    const distance = overscrollBefore - overscrollAfter;
    return distance;
}

/**
 * Assigns offset to state by given distance coordinates
 */
function dragOffsetSnapshotProcedure(fromX, toX) {
    return ({ state, environment }) => {
        const result = dragOffsetSnapshot(fromX, toX, state.offset, state.config.widthMode, state.config.alignMode, state.config.shouldLoop, getViewportWidth(state), getViewportWidthInPx(state), state.config.slideWidth, state.slides.length * state.config.slideWidth, environment.maxOverscroll ?? null, state.dragBezierFn, state.invertedDragBezierFn);
        const modifiedState = {
            ...state,
            offset: result,
        };
        return new ContinueWith(modifiedState);
    };
}

class BreakWith {
    constructor(state, procedureState = null) {
        this.state = state;
        this.procedureState = procedureState;
        this.shouldBreakProcedure = true;
    }
}

/**
 * Would break procedure whether drag is disabled
 */
function postponeDragEnabledProcedure() {
    return ({ state }) => {
        if (state?.config?.dragEnabled) {
            return new ContinueWith(state);
        }
        return new BreakWith(state);
    };
}

/**
 * Processes state after single tick of drag event
 */
function dragProcedure(fromX, toX) {
    return procedurePipe('drag', postponeDragEnabledProcedure(), animationOffsetSnapshotProcedure(), destroyAnimationProcedure(), dragOffsetSnapshotProcedure(fromX, toX), markVisibleAndActiveProcedure(), shuffleSlidesProcedure(), calculateActiveSlideProcedure(), markVisibleAndActiveProcedure());
}

/**
 * Processes state when drag event starts
 */
function dragStartProcedure() {
    return procedurePipe('dragStart', postponeDragEnabledProcedure(), disableAutoplayProcedure("drag" /* AutoplaySuspender.DRAG */));
}

/**
 * Finds slide that is marked as `isActive` and returns its index
 */
function findSlideIndex(slides, newItemIndex, currentSlideIndex) {
    if (!slides) {
        return 0;
    }
    const currentActiveSlide = slides[currentSlideIndex];
    if (!currentActiveSlide) {
        return 0;
    }
    const currentItemIndex = currentActiveSlide.itemIndex;
    let targetSlideIndex = null;
    if (newItemIndex === currentItemIndex || slides.length <= 1) {
        return currentSlideIndex;
    }
    else if (newItemIndex > currentItemIndex) {
        targetSlideIndex =
            iterateRightSide(slides, newItemIndex, currentSlideIndex)
                || iterateLeftSide(slides, newItemIndex, currentSlideIndex);
    }
    else {
        targetSlideIndex =
            iterateLeftSide(slides, newItemIndex, currentSlideIndex)
                || iterateRightSide(slides, newItemIndex, currentSlideIndex);
    }
    return targetSlideIndex?.foundIndex ?? 0;
}
function iterateRightSide(slides, newItemIndex, currentSlideIndex) {
    for (let i = currentSlideIndex + 1; i < slides.length; i++) {
        if (slides[i].itemIndex === newItemIndex) {
            return { foundIndex: i };
        }
    }
    return null;
}
function iterateLeftSide(slides, newItemIndex, currentSlideIndex) {
    for (let i = currentSlideIndex - 1; i >= 0; i--) {
        if (slides[i].itemIndex === newItemIndex) {
            return { foundIndex: i };
        }
    }
    return null;
}

/**
 * Assigns slide index by given item index, breaks procedure if slide index
 * stays the same
 */
function findSlideIndexProcedure(itemIndex) {
    return ({ state }) => {
        const result = findSlideIndex(state.slides, itemIndex ?? state.activeItemIndex, state.activeSlideIndex);
        if (result === state.activeSlideIndex) {
            return new BreakWith(state);
        }
        const modifiedState = {
            ...state,
            activeSlideIndex: result,
            activeItemIndex: itemIndex,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Guard for assigning item index when no slides are available.
 * Would break procedure on such occasion.
 */
function postponeItemIndexProcedure(newItemIndex) {
    return ({ state }) => {
        const hasSlides = Boolean(state.slides.length);
        const modifiedState = {
            ...state,
            postponedItemIndex: hasSlides
                ? null
                : newItemIndex,
        };
        return hasSlides
            ? new ContinueWith(modifiedState)
            : new BreakWith(modifiedState);
    };
}

/** Assigns item index and shuffles slides accordingly */
function goToProcedure(newItemIndex) {
    return procedurePipe('goTo', postponeItemIndexProcedure(newItemIndex), enableAutoplayProcedure(), findSlideIndexProcedure(newItemIndex), setSlideIndexProcedure());
}

/**
 * Fills empty carousel with slides without any
 * alignments
 */
function initializeCarousel(items, idGenerator) {
    if (!items || !items.length || !idGenerator) {
        return [];
    }
    const newSlides = new Array(items.length);
    for (let i = 0; i < items.length; i++) {
        const state = {
            item: items[i],
            isActive: i === 0,
            inViewport: false,
            activeOnTheLeft: i > 0,
            activeOnTheRight: false,
        };
        newSlides[i] = new CarouselSlide(idGenerator.next(), i, state);
    }
    return newSlides;
}

/**
 * Creates new slide set from scratch
 */
function initializeCarouselProcedure() {
    return ({ state, environment }) => {
        const result = initializeCarousel(state.config.items, environment.slideIdGenerator ?? null);
        const modifiedState = {
            ...state,
            slides: result,
            offset: 0,
            activeSlideIndex: 0,
            slideIndex: state.slideIndex + 1,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Would break procedure when carousel is not ready
 * for first initialization
 */
function postponeInitializationProcedure() {
    return ({ state }) => {
        const phase = state?.initializationState ?? new InitializationState();
        if (phase.configInitialized && phase.viewportWidthInitialized) {
            return new ContinueWith(state);
        }
        return new BreakWith(state);
    };
}

// Reference: https://easings.net/ru
// Standard ease
const ANIMATION_BEZIER_ARGS = [0.25, 0.1, 0.25, 1];
// Ease out quad
const DRAG_BEZIER_ARGS = [0.25, 0.46, 0.45, 0.94];
const DRAG_BEZIER_FN = bezier(DRAG_BEZIER_ARGS[0], DRAG_BEZIER_ARGS[1], DRAG_BEZIER_ARGS[2], DRAG_BEZIER_ARGS[3]);
const INVERTED_DRAG_BEZIER_FN = bezier(1 - DRAG_BEZIER_ARGS[0], 1 - DRAG_BEZIER_ARGS[1], 1 - DRAG_BEZIER_ARGS[2], 1 - DRAG_BEZIER_ARGS[3]);
const ANIMATION_BEZIER_FN = bezier(ANIMATION_BEZIER_ARGS[0], ANIMATION_BEZIER_ARGS[1], ANIMATION_BEZIER_ARGS[2], ANIMATION_BEZIER_ARGS[3]);
/**
 * Creates bezier functions for different purposes
 */
function setBeziersProcedure() {
    return ({ state }) => {
        const modifiedState = {
            ...state,
            dragBezierFn: state.dragBezierFn ?? DRAG_BEZIER_FN,
            invertedDragBezierFn: state.invertedDragBezierFn ?? INVERTED_DRAG_BEZIER_FN,
            animationBezierFn: state.animationBezierFn ?? ANIMATION_BEZIER_FN,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Set state as initialized
 */
function setFirstInitializationProcedure() {
    return ({ state }) => {
        state.initializationState.firstInitalization = true;
        return new ContinueWith(state);
    };
}

function removePostponedIndexProcedure() {
    return ({ state }) => {
        const modifiedState = {
            ...state,
            postponedItemIndex: null,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Saves freshly selected item index till later
 * until carousel is initialized
 */
function setPostponedIndexProcedure() {
    return ({ state }) => {
        if (state.slides.length && state.postponedItemIndex) {
            const itemIndex = state.postponedItemIndex;
            return procedurePipe('setPostponedIndex', goToProcedure(itemIndex), removePostponedIndexProcedure());
        }
        return new ContinueWith(state);
    };
}

/**
 * Creates slides from scratch
 */
function initializeProcedure() {
    return procedurePipe('initialize', postponeInitializationProcedure(), setFirstInitializationProcedure(), initializeCarouselProcedure(), calculateOffsetProcedure(), markVisibleAndActiveProcedure(), shuffleSlidesProcedure(), markVisibleAndActiveProcedure(), destroyAnimationProcedure(), enableAutoplayProcedure(), calculateActiveSlideProcedure(), setBeziersProcedure(), setPostponedIndexProcedure());
}

/**
 * Assigns carousel config, works as part of multiphase carousel initialization
 */
function setConfigProcedure(config) {
    return ({ state }) => {
        const modifiedState = {
            ...state,
            config,
            initializationState: {
                ...state.initializationState,
                configInitialized: true,
            },
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Assigns config and executes initialization effects
 */
function initializeConfigProcedure(newConfig) {
    return procedurePipe('initializeConfig', setConfigProcedure(newConfig), initializeProcedure());
}

/**
 * Saves DOM element containers for carousel as part of initialization phase
 */
function setContainersProcedure(widthContainer, animatableContainer) {
    return ({ state }) => {
        const modifiedState = {
            ...state,
            widthContainer,
            animatableContainer,
            initializationState: {
                ...state.initializationState,
                viewportWidthInitialized: true,
            },
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Assigns specified DOM containers to carousel state
 */
function initializeContainersProcedure(widthContainer, animatableContainer) {
    return procedurePipe('initializeContainers', setContainersProcedure(widthContainer, animatableContainer), initializeProcedure());
}

/**
 * Returns next slide index as if user
 * clicked "next" button
 */
function getNextIndex(slidesLength, activeSlideIndex, shouldLoop) {
    let newIndex = activeSlideIndex + 1;
    if (newIndex >= slidesLength) {
        newIndex = shouldLoop
            ? 0
            : slidesLength - 1;
    }
    return newIndex;
}

/**
 * Increments current item index
 */
function getNextIndexProcedure() {
    return ({ state }) => {
        const result = getNextIndex(state.slides.length, state.activeSlideIndex, state.config.shouldLoop);
        if (result === state.activeSlideIndex) {
            return new BreakWith(state);
        }
        const modifiedState = {
            ...state,
            activeSlideIndex: result,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Increments current slide
 */
function nextProcedure(omitAutoplayReset = false) {
    return procedurePipe('next', omitAutoplayReset ? null : enableAutoplayProcedure(), getNextIndexProcedure(), setSlideIndexProcedure());
}

/**
 * Returns previous slide index as if user
 * clicked "prev" button
 */
function getPrevIndex(slidesLength, activeSlideIndex, shouldLoop) {
    let newIndex = activeSlideIndex - 1;
    if (newIndex < 0) {
        newIndex = shouldLoop
            ? slidesLength - 1
            : 0;
    }
    return newIndex;
}

/**
 * Decrements current item index
 */
function getPrevIndexProcedure() {
    return ({ state }) => {
        const result = getPrevIndex(state.slides.length, state.activeSlideIndex, state.config.shouldLoop);
        if (result === state.activeSlideIndex) {
            return new BreakWith(state);
        }
        const modifiedState = {
            ...state,
            activeSlideIndex: result,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Decrements current slide
 */
function prevProcedure() {
    return procedurePipe('prev', enableAutoplayProcedure(), getPrevIndexProcedure(), setSlideIndexProcedure());
}

/**
 * Programmaticaly recalculates current state
 */
function recalculateProcedure() {
    return procedurePipe('recalculate', calculateOffsetProcedure(), markVisibleAndActiveProcedure(), shuffleSlidesProcedure(), markVisibleAndActiveProcedure());
}

/**
 * Assigns TemplateRef where slides would be rendered
 */
function setTemplateProcedure(template) {
    return ({ state }) => {
        const modifiedState = {
            ...state,
            template,
        };
        return new ContinueWith(modifiedState);
    };
}

/**
 * Short swipe might not change slide to next/prev.
 * This const specifies how much (% of viewport) swipe
 * should overcome to trigger next/prev slide change.
 */
const MAX_SWIPE_THRESHOLD = 15;
/**
 * How much % user can stretch carousel, when there's no more
 * drag available
 */
const MAX_OVERSCROLL = 10;
class CarouselService {
    constructor(slideIdGenerator, 
    // tslint:disable-next-line: ban-types
    platformId, ngZone) {
        this.slideIdGenerator = slideIdGenerator;
        this.platformId = platformId;
        this.ngZone = ngZone;
        this.carouselState$ = new BehaviorSubject(new CarouselState());
        /** Describes constant entities for procedures */
        this.procedureEnvironment = {
            slideIdGenerator: this.slideIdGenerator,
            isBrowser: isPlatformBrowser(this.platformId),
            autoplayAction: this.next.bind(this, true),
            afterAnimationAction: this.cleanup.bind(this),
            animationBezierArgs: ANIMATION_BEZIER_ARGS,
            swipeThreshold: MAX_SWIPE_THRESHOLD,
            maxOverscroll: MAX_OVERSCROLL,
            zone: this.ngZone,
        };
    }
    ngOnDestroy() {
        this.carouselState$.getValue()?.autoplay?.autoplaySubscription?.unsubscribe();
    }
    getItemIndex() {
        return this.carouselState$.getValue().activeItemIndex;
    }
    carouselStateChanges() {
        return this.carouselState$.asObservable();
    }
    setItemIndex(newItemIndex) {
        this.apply(goToProcedure(newItemIndex));
    }
    prev() {
        this.apply(prevProcedure());
    }
    /**
     * @param omitAutoplayReset whether autoplay timer should not be resetted
     */
    next(omitAutoplayReset = false) {
        this.apply(nextProcedure(omitAutoplayReset));
    }
    recalculate() {
        this.apply(recalculateProcedure());
    }
    /** Update state to announce that drag sequence just started */
    dragStart() {
        this.apply(dragStartProcedure());
    }
    /**
     * Update state to announce that drag sequence just ended
     * and perform necessary cleanups
     */
    dragEnd(passedDistance) {
        this.apply(dragEndProcedure(passedDistance));
    }
    /** Process single drag tick with given from and to coordinates */
    drag(fromX, toX) {
        this.apply(dragProcedure(fromX, toX));
    }
    setSlideTemplate(newTemplateRef) {
        this.apply(setTemplateProcedure(newTemplateRef));
    }
    disableAutoplay(suspender) {
        this.apply(disableAutoplayProcedure(suspender));
    }
    /**
     * Starts new autoplay timer
     */
    enableAutoplay(suspender = null) {
        this.apply(enableAutoplayProcedure(suspender));
    }
    setContainers(widthContainer, animatableContainer) {
        this.apply(initializeContainersProcedure(widthContainer, animatableContainer));
    }
    setConfig(newConfig) {
        this.apply(initializeConfigProcedure(newConfig));
    }
    cleanup() {
        this.apply(cleanupProcedure());
    }
    /**
     * Applies specified procedure to carousel state
     */
    apply(procedure) {
        const state = Object.assign({}, this.carouselState$.getValue());
        const result = procedurePipe('applier', procedure)({ state, procedureState: {}, environment: this.procedureEnvironment });
        this.carouselState$.next(result.state);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselService, deps: [{ token: SLIDE_ID_GENERATOR }, { token: PLATFORM_ID }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: IdGenerator, decorators: [{
                    type: Inject,
                    args: [SLIDE_ID_GENERATOR]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.NgZone }]; } });

/** Wraps stream with zone */
function enterZone(zone) {
    return (source) => new Observable((observer) => source.subscribe({
        next: (value) => zone.run(() => observer.next(value)),
        error: (err) => observer.error(err),
        complete: () => observer.complete()
    }));
}

const listenerOptions = { passive: true, capture: true };
const captureOptions = { capture: true };
class PanRecognizerService {
    constructor(zone, rendererFactory, carousel, document) {
        this.zone = zone;
        this.rendererFactory = rendererFactory;
        this.carousel = carousel;
        this.document = document;
        // Be advised that pan does not start when user presses the pointer,
        // it should move by a certain threshold first before recognizing
        // as a pan gesture. Otherwise, it is just a click.
        /**
         * Tells for the whole chain (pointer events -> click events),
         * that pan is still ongoing (even after pointer is up),
         * so we can prevent ghost clicks
         */
        this.isPanning = false;
        /**
         * Immediately responds to pan event status (is ongoing or not)
         */
        this.isPanningSync = false;
        /** X and Y of pointer when user pressed it */
        this.startCoordinates = null;
        this.lastX = null;
        this.schedule$ = null;
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }
    /** Submits drag event to carousel service whenever one happens */
    listen(element) {
        const listeners = [
            [element, 'pointerdown', this.startDrag.bind(this), listenerOptions],
            [this.document, 'pointermove', this.processDrag.bind(this, element), captureOptions],
            [this.document, 'pointerup', this.endDrag.bind(this, element), captureOptions],
            [this.document, 'pointercancel', this.endDrag.bind(this, element), captureOptions],
        ];
        return new Observable(() => {
            listeners.forEach(([el, name, fn, options]) => {
                this.zone.runOutsideAngular(() => {
                    el.addEventListener(name, fn, options);
                });
            });
            return () => {
                listeners.forEach(([el, name, fn, options]) => {
                    el.removeEventListener(name, fn, options);
                });
            };
        });
    }
    reset() {
        this.startCoordinates = null;
        this.lastX = null;
        this.isPanningSync = false;
        this.schedule$ = asyncScheduler.schedule(() => {
            this.isPanning = false;
            this.schedule$ = null;
        });
    }
    startDrag(event) {
        this.reset();
        this.startCoordinates = [event.pageX, event.pageY];
    }
    processDrag(element, event) {
        if (this.startCoordinates === null) {
            return;
        }
        const eventCoordinates = [event.pageX, event.pageY];
        if (!eventCoordinates) {
            return;
        }
        event.preventDefault();
        if (this.isPanningSync) {
            this.carousel?.drag(this.lastX || 0, Math.round(eventCoordinates[0]));
            this.lastX = Math.round(eventCoordinates[0]);
            return;
        }
        const deltaX = Math.abs(eventCoordinates[0] - this.startCoordinates[0]);
        const deltaY = Math.abs(eventCoordinates[1] - this.startCoordinates[1]);
        if (deltaX < 10 && deltaY < 10) {
            return;
        }
        if (deltaX < deltaY) {
            this.reset();
            return;
        }
        this.carousel?.dragStart();
        // We should block all scroll attempts during current pan session
        this.renderer.setStyle(element, 'touch-action', 'none');
        this.isPanningSync = true;
        this.isPanning = true;
        this.schedule$?.unsubscribe();
        this.lastX = Math.round(eventCoordinates[0]);
    }
    endDrag(element, event) {
        if (this.startCoordinates === null) {
            return;
        }
        const eventCoordinates = [event.pageX, event.pageY];
        const x = eventCoordinates?.[0] ?? this.lastX ?? null;
        const startX = this.startCoordinates[0];
        this.reset();
        if (x === null || !this.isPanning) {
            return;
        }
        event.preventDefault();
        this.carousel?.dragEnd(Math.round(x - startX));
        this.renderer.removeStyle(element, 'touch-action');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: PanRecognizerService, deps: [{ token: i0.NgZone }, { token: i0.RendererFactory2 }, { token: CarouselService, optional: true }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: PanRecognizerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: PanRecognizerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.RendererFactory2 }, { type: CarouselService, decorators: [{
                    type: Optional
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class CarouselError extends Error {
    constructor(message) {
        super();
        this.message = message;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

const selectors = [
    `a[href]`,
    `button`,
    `input`,
    `select`,
    `textarea`,
    `area[href]`,
    `[tabindex]`,
].map((selector) => `${selector}:not([tabindex="-1"]):not([disabled])`);
const selectorsString = selectors.join(', ');
class FocusBlockDirective {
    constructor(elementRef, platformId) {
        this.elementRef = elementRef;
        this.platformId = platformId;
        this.untabbable = false;
        /** Whether focus inside carousel */
        this.untabbableFocused = false;
        this.lastTabindexValueMap = new Map();
        this.viewInitiated = false;
        this.mutationObserver = null;
    }
    ngOnChanges(changes) {
        if (changes.untabbable && this.viewInitiated) {
            const change = changes.untabbable;
            if (change.currentValue === change.previousValue) {
                return;
            }
            change.currentValue
                ? this.blockTabindex()
                : this.unblockTabindex();
            if (this.untabbableFocused && isPlatformBrowser(this.platformId)) {
                for (let selector of selectors) {
                    const el = this.elementRef.nativeElement.querySelector(selector);
                    if (el) {
                        el.focus();
                        break;
                    }
                }
            }
        }
    }
    ngAfterViewInit() {
        this.viewInitiated = true;
        this.untabbable
            ? this.blockTabindex()
            : this.unblockTabindex();
    }
    ngOnDestroy() {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
    }
    blockTabindex() {
        const elements = Array.from(this.elementRef.nativeElement.querySelectorAll('*'));
        for (const element of elements) {
            this.blockElement(element);
        }
        if (typeof window !== 'undefined' && 'MutationObserver' in window) {
            this.mutationObserver = new MutationObserver((mutationList) => {
                const changesArray = Array.from(mutationList);
                for (const change of changesArray) {
                    if (change.type === 'attributes') {
                        this.blockElement(change.target);
                    }
                    else if (change.type === 'childList') {
                        change.addedNodes.forEach((element) => {
                            this.blockElement(element);
                        });
                    }
                }
            });
            this.mutationObserver.observe(this.elementRef.nativeElement, {
                attributeFilter: ['tabindex'],
                attributes: true,
                childList: true,
                subtree: true,
            });
        }
    }
    unblockTabindex() {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
        const elements = Array.from(this.elementRef.nativeElement.querySelectorAll('*'));
        for (const element of elements) {
            this.unblockElement(element);
        }
    }
    blockElement(element) {
        if (element.nodeType === Node.ELEMENT_NODE // Check only elements
            && element.matches(selectorsString)) {
            const currentTabindexValue = element.getAttribute('tabindex');
            this.lastTabindexValueMap.set(element, currentTabindexValue);
            if (currentTabindexValue !== '-1') {
                element.setAttribute('tabindex', '-1');
            }
        }
    }
    unblockElement(element) {
        const lastTabIndex = this.lastTabindexValueMap.get(element);
        if (typeof lastTabIndex === 'number') {
            element.setAttribute('tabindex', lastTabIndex);
        }
        else {
            element.removeAttribute('tabindex');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: FocusBlockDirective, deps: [{ token: i0.ElementRef }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.2", type: FocusBlockDirective, selector: "[untabbable]", inputs: { untabbable: "untabbable", untabbableFocused: "untabbableFocused" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: FocusBlockDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[untabbable]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; }, propDecorators: { untabbable: [{
                type: Input
            }], untabbableFocused: [{
                type: Input
            }] } });

class CarouselEngineComponent {
    constructor(carousel, elementRef, renderer, zone, panRecognizer, cdr, document, 
    // tslint:disable-next-line: ban-types
    platformId) {
        this.carousel = carousel;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.zone = zone;
        this.panRecognizer = panRecognizer;
        this.cdr = cdr;
        this.document = document;
        this.platformId = platformId;
        this.galleryRef = null;
        this.slides = null;
        this.templateRef = null;
        this.focused = false;
        this.destroyed$ = new Subject();
        this.mouseEnterDestructor = null;
        this.mouseLeaveDestructor = null;
        this.keyboardListener = null;
        this.containerScrollListener = null;
        this.visibilityListener = null;
        this.htmlElement = this.elementRef.nativeElement;
        this.cdr.detach();
    }
    ngOnInit() {
        this.listenToAutoplay();
        this.listenToDragEvents();
        this.listenToResizeEvents();
        this.listenToKeyEvents();
        this.listenToScrollEvents();
        this.listenToVisibilityEvents();
        this.listenSlideChanges();
        this.listenContainerStyleChanges();
        this.listenTemplateChanges();
        if (this.galleryRef?.nativeElement) {
            this.carousel.setContainers(this.htmlElement, this.galleryRef?.nativeElement ?? null);
        }
        else {
            throw new CarouselError('Error initializing ng-carousel-cdk containers');
        }
    }
    ngOnDestroy() {
        this.mouseEnterDestructor?.();
        this.mouseLeaveDestructor?.();
        this.keyboardListener?.();
        this.containerScrollListener?.();
        this.visibilityListener?.();
        this.destroyed$.next();
        this.destroyed$.complete();
    }
    trackByFn(index, item) {
        return item.id;
    }
    focusIn() {
        this.focused = true;
        this.carousel.disableAutoplay("focus" /* AutoplaySuspender.FOCUS */);
    }
    focusOut() {
        this.focused = false;
        this.carousel.enableAutoplay("focus" /* AutoplaySuspender.FOCUS */);
    }
    listenToAutoplay() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.carousel.carouselStateChanges()
            .pipe(map((state) => state.config.autoplayEnabled), distinctUntilChanged(), takeUntil(this.destroyed$))
            .subscribe((autoplayEnabled) => {
            this.mouseEnterDestructor?.();
            this.mouseLeaveDestructor?.();
            if (!autoplayEnabled) {
                return;
            }
            this.zone.runOutsideAngular(() => {
                this.mouseEnterDestructor = this.renderer.listen(this.htmlElement, 'mouseenter', () => this.carousel.disableAutoplay("mouse" /* AutoplaySuspender.MOUSE */));
                this.mouseLeaveDestructor = this.renderer.listen(this.htmlElement, 'mouseleave', () => this.carousel.enableAutoplay("mouse" /* AutoplaySuspender.MOUSE */));
            });
        });
    }
    listenToDragEvents() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.carousel.carouselStateChanges()
            .pipe(map((state) => state.config.dragEnabled), distinctUntilChanged(), switchMap((dragEnabled) => dragEnabled
            ? this.panRecognizer.listen(this.htmlElement)
            : NEVER), takeUntil(this.destroyed$))
            .subscribe();
    }
    listenToResizeEvents() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.carousel.carouselStateChanges()
            .pipe(map((state) => state.config.shouldRecalculateOnResize), distinctUntilChanged(), switchMap((shouldRecalculate) => shouldRecalculate
            ? this.fromElementResize(this.elementRef.nativeElement)
            : NEVER), debounce(() => this.carousel.carouselStateChanges()
            .pipe(map((state) => state.config.recalculateDebounce), distinctUntilChanged(), switchMap((debounceTime) => interval(debounceTime)))), takeUntil(this.destroyed$))
            .subscribe(() => {
            this.carousel.recalculate();
        });
    }
    listenToKeyEvents() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.carousel.carouselStateChanges()
            .pipe(map((state) => state.config.allowKeyboardNavigation), distinctUntilChanged(), takeUntil(this.destroyed$))
            .subscribe((canListen) => {
            this.keyboardListener?.();
            if (!canListen) {
                return;
            }
            this.keyboardListener = this.renderer.listen(this.htmlElement, 'keydown', (event) => {
                const key = event.key.toLowerCase();
                if (['arrowright', 'right'].includes(key)) {
                    this.carousel.next();
                }
                else if (['arrowleft', 'left'].includes(key)) {
                    this.carousel.prev();
                }
            });
        });
    }
    /**
     * Horizontal scroll might accidentaly happen on parent container
     * when pressing arrow buttons too fast. We should return
     * container to initial position when that happens.
     */
    listenToScrollEvents() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.containerScrollListener = this.renderer.listen(this.htmlElement, 'scroll', () => {
            this.htmlElement.scrollTo(0, 0);
        });
    }
    listenToVisibilityEvents() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        if (this.document.hidden) {
            this.carousel.disableAutoplay("blur" /* AutoplaySuspender.BLUR */);
        }
        this.visibilityListener = this.renderer.listen(this.document, 'visibilitychange', () => {
            if (this.document.hidden) {
                this.carousel.disableAutoplay("blur" /* AutoplaySuspender.BLUR */);
            }
            else {
                this.carousel.enableAutoplay("blur" /* AutoplaySuspender.BLUR */);
            }
        });
    }
    fromElementResize(element) {
        if (!element || !isPlatformBrowser(this.platformId)) {
            return NEVER;
        }
        return new Observable((subscriber) => {
            const observer = new ResizeObserver(() => {
                subscriber.next();
            });
            observer.observe(element);
            return () => {
                observer.disconnect();
            };
        });
    }
    listenSlideChanges() {
        this.carousel.carouselStateChanges()
            .pipe(distinctUntilKeyChanged('slideIndex'), map((state) => state.slides), takeUntil(this.destroyed$))
            .subscribe((slides) => {
            this.slides = slides ?? [];
            this.cdr.detectChanges();
        });
    }
    listenContainerStyleChanges() {
        this.carousel.carouselStateChanges()
            .pipe(switchMap((state) => combineLatest([
            of(`translateX(${state.offset}${state.config.widthMode})`)
                .pipe(distinctUntilChanged()),
            of(`${state.config.slideWidth}${state.config.widthMode}`)
                .pipe(distinctUntilChanged()),
            of(state.animatableContainer)
                .pipe(distinctUntilChanged()),
        ])), takeUntil(this.destroyed$))
            .subscribe(([transform, width, container]) => {
            if (container) {
                this.renderer.setProperty(container, 'style', `transform:${transform};--ng-carousel-sw:${width}`);
            }
        });
    }
    listenTemplateChanges() {
        this.carousel.carouselStateChanges()
            .pipe(map((state) => state.template), distinctUntilChanged(), takeUntil(this.destroyed$))
            .subscribe((template) => {
            this.templateRef = template;
            this.cdr.detectChanges();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselEngineComponent, deps: [{ token: CarouselService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.NgZone }, { token: PanRecognizerService }, { token: i0.ChangeDetectorRef }, { token: DOCUMENT }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.2", type: CarouselEngineComponent, selector: "carousel-engine", viewQueries: [{ propertyName: "galleryRef", first: true, predicate: ["galleryRef"], descendants: true, static: true }], ngImport: i0, template: "<ul\n    #galleryRef\n    (focusin)=\"focusIn()\"\n    (focusout)=\"focusOut()\"\n    class=\"ng-carousel-block\">\n    <li\n        *ngFor=\"let slide of slides; trackBy: trackByFn;\"\n        [untabbable]=\"!slide.options.isActive\"\n        [untabbableFocused]=\"focused\"\n        class=\"ng-carousel-slide\">\n        <ng-container *ngTemplateOutlet=\"templateRef; context: slide.context;\"></ng-container>\n    </li>\n</ul>\n", styles: [".ng-carousel-block{--ng-carousel-sw: 0;width:100%;height:100%;display:flex;margin:0;padding:0;will-change:transform}.ng-carousel-slide{flex:none;width:var(--ng-carousel-sw, 0);list-style:none}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: FocusBlockDirective, selector: "[untabbable]", inputs: ["untabbable", "untabbableFocused"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselEngineComponent, decorators: [{
            type: Component,
            args: [{ selector: 'carousel-engine', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<ul\n    #galleryRef\n    (focusin)=\"focusIn()\"\n    (focusout)=\"focusOut()\"\n    class=\"ng-carousel-block\">\n    <li\n        *ngFor=\"let slide of slides; trackBy: trackByFn;\"\n        [untabbable]=\"!slide.options.isActive\"\n        [untabbableFocused]=\"focused\"\n        class=\"ng-carousel-slide\">\n        <ng-container *ngTemplateOutlet=\"templateRef; context: slide.context;\"></ng-container>\n    </li>\n</ul>\n", styles: [".ng-carousel-block{--ng-carousel-sw: 0;width:100%;height:100%;display:flex;margin:0;padding:0;will-change:transform}.ng-carousel-slide{flex:none;width:var(--ng-carousel-sw, 0);list-style:none}\n"] }]
        }], ctorParameters: function () { return [{ type: CarouselService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.NgZone }, { type: PanRecognizerService }, { type: i0.ChangeDetectorRef }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; }, propDecorators: { galleryRef: [{
                type: ViewChild,
                args: ['galleryRef', { static: true }]
            }] } });

function idGeneratorFactory() {
    return new IdGenerator();
}
class CarouselComponent {
    set slideRef(newSlideRef) {
        this.carousel.setSlideTemplate(newSlideRef
            ? newSlideRef.templateRef
            : null);
    }
    set config(newConfig) {
        const configInstance = new CompleteCarouselConfig(newConfig);
        this.carousel.setConfig(configInstance);
    }
    get slideIndex() {
        return this.carousel.getItemIndex();
    }
    constructor(carousel, ngZone) {
        this.carousel = carousel;
        this.ngZone = ngZone;
        this.itemIndexChange = this.carousel.carouselStateChanges()
            .pipe(map((state) => state.activeItemIndex), distinctUntilChanged(), enterZone(this.ngZone));
    }
    next() {
        this.carousel.next();
    }
    prev() {
        this.carousel.prev();
    }
    setIndex(newIndex) {
        this.carousel.setItemIndex(newIndex);
    }
    /**
     * Programmaticaly recalculates carousel position in case of
     * container size changes or other size interactions
     */
    recalculate() {
        this.carousel.recalculate();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselComponent, deps: [{ token: CarouselService }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.2", type: CarouselComponent, selector: "ng-carousel", inputs: { config: "config" }, outputs: { itemIndexChange: "itemIndexChange" }, providers: [
            CarouselService,
            PanRecognizerService,
            {
                provide: SLIDE_ID_GENERATOR,
                useFactory: idGeneratorFactory,
            },
            {
                provide: ANIMATION_ID_GENERATOR,
                useFactory: idGeneratorFactory,
            },
        ], queries: [{ propertyName: "slideRef", first: true, predicate: CarouselSlideDirective, descendants: true }], exportAs: ["ngCarousel"], ngImport: i0, template: "<carousel-engine class=\"carousel-engine\"></carousel-engine>\n", styles: [".carousel-engine{display:block;width:100%;height:100%;overflow:hidden;touch-action:pan-y;-webkit-user-select:none;-moz-user-select:none;user-select:none;-webkit-user-drag:none;-webkit-tap-highlight-color:rgba(0,0,0,0)}\n"], dependencies: [{ kind: "component", type: CarouselEngineComponent, selector: "carousel-engine" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ng-carousel', providers: [
                        CarouselService,
                        PanRecognizerService,
                        {
                            provide: SLIDE_ID_GENERATOR,
                            useFactory: idGeneratorFactory,
                        },
                        {
                            provide: ANIMATION_ID_GENERATOR,
                            useFactory: idGeneratorFactory,
                        },
                    ], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, exportAs: 'ngCarousel', template: "<carousel-engine class=\"carousel-engine\"></carousel-engine>\n", styles: [".carousel-engine{display:block;width:100%;height:100%;overflow:hidden;touch-action:pan-y;-webkit-user-select:none;-moz-user-select:none;user-select:none;-webkit-user-drag:none;-webkit-tap-highlight-color:rgba(0,0,0,0)}\n"] }]
        }], ctorParameters: function () { return [{ type: CarouselService }, { type: i0.NgZone }]; }, propDecorators: { slideRef: [{
                type: ContentChild,
                args: [CarouselSlideDirective]
            }], config: [{
                type: Input
            }], itemIndexChange: [{
                type: Output
            }] } });

class PreventGhostClickDirective {
    constructor(elementRef, zone, rendererFactory, document, panRecognizer) {
        this.elementRef = elementRef;
        this.zone = zone;
        this.rendererFactory = rendererFactory;
        this.document = document;
        this.panRecognizer = panRecognizer;
        this.parentElement = null;
        this.preventFn = this.preventEvent.bind(this);
        this.opts = { capture: true };
        this.element = this.elementRef.nativeElement;
        this.requiredPanRecognizer = this.panRecognizer
            || new PanRecognizerService(this.zone, this.rendererFactory, null, this.document);
        this.subscriptions$ = new Subscription();
    }
    ngOnInit() {
        this.element.addEventListener('click', this.preventFn, this.opts);
        if (!this.panRecognizer) {
            const subscription$ = this.requiredPanRecognizer.listen(this.parentElement || this.elementRef.nativeElement).subscribe();
            this.subscriptions$.add(subscription$);
        }
    }
    ngOnDestroy() {
        this.element.removeEventListener('click', this.preventFn, this.opts);
        this.subscriptions$.unsubscribe();
    }
    preventEvent(event) {
        if (this.requiredPanRecognizer?.isPanning) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: PreventGhostClickDirective, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }, { token: i0.RendererFactory2 }, { token: DOCUMENT }, { token: PanRecognizerService, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.2", type: PreventGhostClickDirective, selector: "[ngCarouselPreventGhostClick]", inputs: { parentElement: ["ngCarouselPreventGhostClick", "parentElement"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: PreventGhostClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngCarouselPreventGhostClick]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: i0.RendererFactory2 }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: PanRecognizerService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { parentElement: [{
                type: Input,
                args: ['ngCarouselPreventGhostClick']
            }] } });

class CarouselPreventGhostClickModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselPreventGhostClickModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.2", ngImport: i0, type: CarouselPreventGhostClickModule, declarations: [PreventGhostClickDirective], imports: [CommonModule], exports: [PreventGhostClickDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselPreventGhostClickModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselPreventGhostClickModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        PreventGhostClickDirective,
                    ],
                    exports: [
                        PreventGhostClickDirective,
                    ],
                }]
        }] });

class CarouselModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.2", ngImport: i0, type: CarouselModule, declarations: [CarouselComponent,
            CarouselSlideDirective,
            CarouselEngineComponent,
            FocusBlockDirective], imports: [NgForOf,
            AsyncPipe,
            NgTemplateOutlet,
            CarouselPreventGhostClickModule], exports: [CarouselComponent,
            CarouselSlideDirective,
            CarouselPreventGhostClickModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselModule, imports: [CarouselPreventGhostClickModule, CarouselPreventGhostClickModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.2", ngImport: i0, type: CarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        NgForOf,
                        AsyncPipe,
                        NgTemplateOutlet,
                        CarouselPreventGhostClickModule,
                    ],
                    declarations: [
                        CarouselComponent,
                        CarouselSlideDirective,
                        CarouselEngineComponent,
                        FocusBlockDirective,
                    ],
                    exports: [
                        CarouselComponent,
                        CarouselSlideDirective,
                        CarouselPreventGhostClickModule,
                    ],
                }]
        }] });

/*
 * Public API Surface of ng-carousel
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CarouselAlignMode, CarouselComponent, CarouselModule, CarouselPreventGhostClickModule, CarouselSlideDirective, CarouselWidthMode, PreventGhostClickDirective, idGeneratorFactory };
